import HighlightOffOutlined from '@mui/icons-material/HighlightOffOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { reduce, sortedIndex } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

import Tabs, { TabProps } from '~components/Tabs/Tabs';
import type { JobTripEvent } from '~hooks/useJob/models';
import { LoadCycle } from '~hooks/useLoad/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';

import { CycleTimeline } from './components/CycleTimeline';
import { useGetLoadSegments } from './components/hooks/useGetLoadSegments';
import useHoveredLoadStats from './components/hooks/useHoveredLoadStats';
import { useIndicatorValues } from './components/hooks/useIndicatorValues';
import { usePauseSegments } from './components/hooks/usePauseSegments';
import { SpeedTimeline } from './components/SpeedTimeline';
import { TimelineInfoPanel } from './components/TimelineInfoPanel';

enum TimelineType {
  CYCLE = 'cycle',
  SPEED = 'speed',
}

const tabs: TabProps<TimelineType>[] = [
  { label: t('live_map.timeline.cycle'), value: TimelineType.CYCLE },
  { label: t('live_map.timeline.speed'), value: TimelineType.SPEED },
];

interface TimelineProps {
  onHover?: (type: number, movingSiteType?: number) => void;
  onTimelineVisibilityChange?: (visible: boolean) => void;
  pings: LocationHistoryMinimal[];
  movingSitePings?: LocationHistoryMinimal[];
  loadCycles?: LoadCycle[];
  showHoveredLoadStats?: boolean;
  pauseEvents?: JobTripEvent[];
}

// This is the amount of time, in milliseconds, within which a marker is considered
// Hovered over based on the user's mouse position in the timeline graph
const HOVERED_THRESHOLD = 1000;
const getTimeKey = (time: number) => Math.round(time / HOVERED_THRESHOLD);

export const Timeline = ({
  onHover,
  onTimelineVisibilityChange,
  pings,
  movingSitePings,
  loadCycles,
  showHoveredLoadStats = false,
  pauseEvents = [],
}: TimelineProps) => {
  const [timelineType, setTimelineType] = useState(TimelineType.CYCLE);
  const [currentPing, setCurrentPing] = useState<LocationHistoryMinimal | null>(null);

  const minTime = pings[0].occurredAt.toISOString();
  const maxTime = pings[pings.length - 1].occurredAt.toISOString();

  const { indicatorValues } = useIndicatorValues(minTime, maxTime, []);

  const loadSegments = useGetLoadSegments(pings);
  const pauseSegments = usePauseSegments(pauseEvents, pings);
  const hoveredLoadStats = useHoveredLoadStats(currentPing, loadSegments, loadCycles);
  const showCycleInfo = timelineType === TimelineType.CYCLE;
  const showLoadStats = showHoveredLoadStats && showCycleInfo;

  const timePingMap = useMemo(() => {
    return reduce(
      pings,
      (pingMap, p, index) => {
        const occurredAtThreshold = getTimeKey(p.occurredAt.valueOf());
        pingMap[occurredAtThreshold] = index;
        return pingMap;
      },
      {} as Record<number, number>,
    );
  }, [pings.length]);

  const movingSiteTimePingMap = useMemo(() => {
    if (!movingSitePings || movingSitePings.length === 0) {
      return {};
    }

    return reduce(
      movingSitePings,
      (pingMap, p, index) => {
        const occurredAtThreshold = getTimeKey(p.occurredAt.valueOf());
        pingMap[occurredAtThreshold] = index;
        return pingMap;
      },
      {} as Record<number, number>,
    );
  }, [movingSitePings?.length]);

  const sortedKeys = useMemo(
    () =>
      Object.keys(timePingMap)
        .map(Number)
        .sort((a, b) => a - b),
    [timePingMap],
  );

  const findClosestTimeKey = (time: number): number | null => {
    const timeKey = getTimeKey(time);

    // Find the insertion point
    const index = sortedIndex(sortedKeys, timeKey);

    // If the exact value exists, return it
    if (sortedKeys[index] === timeKey) {
      return timeKey;
    }

    // Otherwise, get the largest value that's less than our target
    const closest = sortedKeys[index - 1];

    // If no valid value was found (i.e., all values are greater than timeKey, or the array is empty)
    if (closest === undefined) {
      return null;
    }

    return closest;
  };

  useEffect(() => {
    if (indicatorValues.indicatorTime && indicatorValues.indicatorVisible) {
      const closestTimeKey = findClosestTimeKey(indicatorValues.indicatorTime);
      const idxPing = closestTimeKey ? timePingMap[closestTimeKey] : null;

      const idxMovingSitePing =
        movingSitePings && movingSitePings.length > 0 && closestTimeKey
          ? (movingSiteTimePingMap[closestTimeKey] ?? undefined)
          : undefined;

      if (idxPing !== null) {
        setCurrentPing(pings[idxPing]);
        onHover?.(idxPing, idxMovingSitePing);
      }
    }
  }, [
    indicatorValues.indicatorTime,
    indicatorValues.indicatorVisible,
    pings,
    timePingMap,
    movingSiteTimePingMap,
    movingSitePings,
  ]);

  return (
    <Box sx={{ mx: 1, mt: 1.5, mb: '25px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap-reverse',
          rowGap: 1,
          columnGap: 2,
          mb: 1,
        }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <TimelineInfoPanel
            ping={currentPing}
            loadNumber={showLoadStats ? (hoveredLoadStats?.loadNumber ?? null) : null}
            cycleSeconds={showLoadStats ? (hoveredLoadStats?.cycleSeconds ?? null) : null}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                bgcolor: '#FDE68A',
                border: `1px solid #FFA000`,
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('live_map.timeline.idle')}
            </Typography>
          </Box>

          {pauseSegments.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  bgcolor: '#A0ABB1',
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('live_map.timeline.paused')}
              </Typography>
            </Box>
          )}

          <Tabs
            onChange={(value) => setTimelineType(value)}
            selected={timelineType}
            tabs={tabs}
            sx={{
              '& .MuiButtonBase-root': {
                minWidth: '60px',
                height: '25px !important',
              },
            }}
          />

          {onTimelineVisibilityChange && (
            <Tooltip title={t('live_map.actions.close_route_history')}>
              <IconButton
                onClick={() => onTimelineVisibilityChange(false)}
                sx={{ width: '24px', height: '24px' }}
              >
                <HighlightOffOutlined sx={{ fontSize: '20px' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {showCycleInfo ? (
        <CycleTimeline
          pings={pings}
          loadSegments={loadSegments}
          hoveredLoadId={hoveredLoadStats?.loadId ?? null}
          pauseSegments={pauseSegments}
        />
      ) : (
        <SpeedTimeline pings={pings} pauseSegments={pauseSegments} />
      )}
    </Box>
  );
};
